import { useMutation, useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';

import type { TCreateModuleRequest } from '@newo/api/gateway';

import { designerV2API } from '@/services/designer/designerV2Client';
import { extractServerErrorMessage } from '@/utils/api/importArchive';

const REGISTRY_MODULE_QUERY_KEYS = {
    registries: () => ['v3-registries'] as const,
    modules: (registryId: string) =>
        ['v3-registry-modules', registryId] as const,
    versions: (registryIdn: string, moduleIdn: string) =>
        ['v3-registry-module-versions', registryIdn, moduleIdn] as const
};

interface CreateModuleVariables {
    registryId: string;
    data: TCreateModuleRequest;
}

export const useV3RegistriesQuery = (options?: { enabled?: boolean }) =>
    useQuery({
        queryKey: REGISTRY_MODULE_QUERY_KEYS.registries(),
        queryFn: async () => designerV2API.listRegistries(),
        enabled: options?.enabled ?? true
    });

export const useV3RegistryModulesQuery = (
    registryId?: string,
    options?: { enabled?: boolean }
) =>
    useQuery({
        queryKey: REGISTRY_MODULE_QUERY_KEYS.modules(registryId ?? ''),
        queryFn: async () => {
            if (!registryId) {
                throw new Error('Registry id is required');
            }
            return designerV2API.listModules(registryId);
        },
        enabled: Boolean(registryId) && (options?.enabled ?? true)
    });

export const useV3RegistryModuleVersionsQuery = (
    registryIdn?: string,
    moduleIdn?: string,
    options?: { enabled?: boolean }
) =>
    useQuery({
        queryKey: REGISTRY_MODULE_QUERY_KEYS.versions(
            registryIdn ?? '',
            moduleIdn ?? ''
        ),
        queryFn: async () => {
            if (!registryIdn) {
                throw new Error('Registry idn is required');
            }
            if (!moduleIdn) {
                throw new Error('Module idn is required');
            }
            return designerV2API.listModuleVersions(registryIdn, moduleIdn, {
                page: 1,
                per: 100
            });
        },
        enabled:
            Boolean(registryIdn) &&
            Boolean(moduleIdn) &&
            (options?.enabled ?? true)
    });

export const useCreateModuleMutation = () =>
    useMutation({
        mutationKey: ['registry-module', 'create'],
        retry: 0,
        mutationFn: async ({ registryId, data }: CreateModuleVariables) =>
            designerV2API.createModule(registryId, data, {
                noErrorMessage: true,
                skipAuthRetry: true
            }),
        onSuccess: () => {
            notifications.show({
                color: 'green',
                message: 'Version released.'
            });
        },
        onError: (error) => {
            notifications.show({
                color: 'red',
                title: 'Release failed',
                message:
                    extractServerErrorMessage(error) ??
                    'Could not release version. Please check the form and try again.',
                autoClose: 8000
            });
        }
    });
